import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { fetchIntegrationsByType } from "../integrations/api";
import findymailLogo from "../../img/findymail.png";
import { INTEGRATION_TYPE_FINDYMAIL } from "../../constants";

interface FindymailIntegration {
  id: string;
  type: string;
  name: string;
  instanceName?: string;
}

export default ({
  actionParameters,
  setActionParameters,
  setValidForm,
  getToken,
  companyId,
}: {
  actionParameters: any;
  setActionParameters: (p: any) => void;
  setValidForm: (b: boolean) => void;
  getToken: () => Promise<string>;
  companyId: string;
}) => {
  const [findymailIntegrations, setFindymailIntegrations] = useState<
    FindymailIntegration[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch Findymail integrations
  useEffect(() => {
    if (!getToken || !companyId) return;

    const loadIntegrations = async () => {
      setLoading(true);
      setError(null);
      try {
        const integrations = await fetchIntegrationsByType(
          companyId,
          INTEGRATION_TYPE_FINDYMAIL,
          getToken
        );
        setFindymailIntegrations(integrations || []);

        // Auto-select if there's only one integration and no value selected
        if (
          integrations.length === 1 &&
          !actionParameters.findymailIntegrationId
        ) {
          setActionParameters({
            ...actionParameters,
            findymailIntegrationId: integrations[0].id,
          });
        }
      } catch (err) {
        console.error("Error fetching Findymail integrations:", err);
        setError("Failed to load Findymail integrations");
        setFindymailIntegrations([]);
      } finally {
        setLoading(false);
      }
    };

    loadIntegrations();
  }, [getToken, companyId]);

  // Validate form
  useEffect(() => {
    const isValid = Boolean(actionParameters.findymailIntegrationId);
    setValidForm(isValid);
  }, [actionParameters.findymailIntegrationId, setValidForm]);

  const handleIntegrationChange = (event: SelectChangeEvent<string>) => {
    setActionParameters({
      ...actionParameters,
      findymailIntegrationId: event.target.value,
    });
  };

  const getIntegrationDisplayName = (
    integration: FindymailIntegration
  ): string => {
    return integration.instanceName || integration.name || "Findymail";
  };

  const renderValue = (selectedId: string) => {
    if (!selectedId) {
      return <em>Select a Findymail integration</em>;
    }
    const integration = findymailIntegrations.find((i) => i.id === selectedId);
    if (!integration) {
      return <em>Select a Findymail integration</em>;
    }
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Avatar
          src={findymailLogo}
          sx={{ width: 24, height: 24 }}
          variant="rounded"
        />
        <span>{getIntegrationDisplayName(integration)}</span>
      </Box>
    );
  };

  return (
    <div>
      <div style={{ marginTop: "24px" }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
        >
          Findymail Integration
          <Tooltip title="Select which Findymail integration to use for email enrichment. The action will find verified emails using your Findymail API key.">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && findymailIntegrations.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No Findymail integrations found. Please set up a Findymail
            integration in the Integrations settings first.
          </Alert>
        )}

        <FormControl
          fullWidth
          disabled={loading || findymailIntegrations.length === 0}
          required
        >
          <InputLabel>Findymail Integration</InputLabel>
          <Select
            value={actionParameters.findymailIntegrationId || ""}
            onChange={handleIntegrationChange}
            label="Findymail Integration"
            renderValue={renderValue}
            startAdornment={
              loading ? (
                <CircularProgress size={20} sx={{ mr: 1 }} />
              ) : undefined
            }
          >
            {loading && (
              <MenuItem value="" disabled>
                Loading integrations...
              </MenuItem>
            )}
            {!loading && findymailIntegrations.length === 0 && (
              <MenuItem value="" disabled>
                No Findymail integrations available
              </MenuItem>
            )}
            {findymailIntegrations.map((integration) => (
              <MenuItem key={integration.id} value={integration.id}>
                <ListItemAvatar sx={{ minWidth: 40 }}>
                  <Avatar
                    src={findymailLogo}
                    sx={{ width: 28, height: 28 }}
                    variant="rounded"
                  />
                </ListItemAvatar>
                <ListItemText primary={getIntegrationDisplayName(integration)} />
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            Email addresses will be found using this Findymail account
          </FormHelperText>
        </FormControl>
      </div>

      <div style={{ marginTop: "24px" }}>
        <Typography variant="subtitle1">Required Fields</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          To find an email, your list must have at least one of these
          combinations:
        </Typography>
        <ul
          style={{
            marginTop: "8px",
            paddingLeft: "20px",
            color: "text.secondary",
          }}
        >
          <li>
            <strong>Name + Company Website/Domain</strong>
          </li>
          <li>
            <strong>LinkedIn URL</strong>
          </li>
        </ul>
      </div>

      <div style={{ marginTop: "24px" }}>
        <Typography variant="subtitle1">Fields Enriched</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          The following fields will be populated (if found and currently empty):
        </Typography>
        <Box sx={{ display: "flex", gap: 4, mt: 1 }}>
          <ul
            style={{
              marginTop: "8px",
              paddingLeft: "20px",
              color: "text.secondary",
            }}
          >
            <li>Email (verified)</li>
            <li>First Name</li>
            <li>Last Name</li>
            <li>LinkedIn URL</li>
            <li>Job Title / Headline</li>
            <li>Location</li>
          </ul>
          <ul
            style={{
              marginTop: "8px",
              paddingLeft: "20px",
              color: "text.secondary",
            }}
          >
            <li>Company Name</li>
            <li>Company Website</li>
            <li>Company Headquarters</li>
            <li>Company Country</li>
          </ul>
        </Box>
      </div>
    </div>
  );
};
